// Pestaña "Casillas" de la pantalla del asistente: una tarjeta por casilla de correo con su estado,
// última corrida, carpetas detectadas, interruptores (activa / modo aprendizaje) y "Leer ahora".
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Play, Loader2, Folder } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Can } from '../../lib/rbac';
import { fmtDateTime, loadMeta, typeLabel, type AssistantMeta, type MailboxWithStats, type LastRun } from './shared';

const REFRESH_MS = 30_000;
const GREEN_MAX_MIN = 15;
const RED_AFTER_MIN = 30;

type Light = 'green' | 'yellow' | 'red' | 'gray';

const LIGHT_DOT: Record<Light, string> = {
  green: 'bg-[var(--color-success)]',
  yellow: 'bg-[var(--color-warning)]',
  red: 'bg-[var(--color-error)]',
  gray: 'bg-[var(--color-text-muted)]',
};

const minutesSince = (iso: string | null | undefined, now: number): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (now - t) / 60_000);
};

function agoText(min: number | null): string {
  if (min === null) return 'nunca';
  if (min < 1) return 'hace menos de 1 min';
  if (min < 60) return `hace ${Math.floor(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `hace ${h} h ${Math.floor(min % 60)} min`;
  return `hace ${Math.floor(h / 24)} días`;
}

function durationText(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${s % 60} s`;
}

/** Semáforo de la casilla (la "última lectura" es el inicio de la última corrida). */
function lightOf(m: MailboxWithStats, now: number): { light: Light; text: string } {
  if (!m.enabled) return { light: 'gray', text: 'Apagada' };
  const err = m.last_error?.trim() || null;
  const isCursorHeld = !!err && err.toLowerCase().startsWith('cursor retenido');
  if (err && !isCursorHeld) return { light: 'red', text: 'Con error' };
  const min = minutesSince(m.last_run?.started_at, now);
  if (min === null || min >= RED_AFTER_MIN) {
    const stuck = m.last_run?.status === 'running';
    return {
      light: 'red',
      text: stuck ? `Lectura en curso hace más de ${RED_AFTER_MIN} min` : `Sin lectura en ${RED_AFTER_MIN} min`,
    };
  }
  if (isCursorHeld) {
    const loadErrors = err!.toLowerCase().includes('errores');
    return { light: 'yellow', text: loadErrors ? 'Correos con error de carga' : 'Leyendo correos atrasados' };
  }
  if (min < GREEN_MAX_MIN) return { light: 'green', text: 'Al día' };
  return { light: 'yellow', text: 'Lectura demorada' };
}

function runStatusText(status: string): string {
  if (status === 'running') return 'en curso';
  if (status === 'done') return 'terminada';
  if (status === 'failed') return 'falló';
  if (status === 'cancelled') return 'cancelada';
  return status;
}

function num(stats: unknown, key: string): number | null {
  if (!stats || typeof stats !== 'object') return null;
  const v = (stats as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : null;
}

/**
 * Texto explicativo de un error de la casilla, sin jerga. El texto técnico original nunca se
 * muestra tal cual: queda solo en el tooltip (title) del texto traducido.
 */
function errorText(err: string): string {
  const low = err.toLowerCase();
  if (low.startsWith('cursor retenido')) {
    if (low.includes('errores')) {
      return 'Algunos correos no se pudieron cargar en la última lectura; se reintentan en la próxima.';
    }
    if (low.includes('tope')) {
      return 'Todavía hay correos atrasados por leer: cada lectura trae una tanda y sigue en la próxima.';
    }
  }
  if (err.includes('Google auth')) {
    return 'Google rechazó el permiso de esta casilla. Revisá la autorización en el panel de Google Workspace.';
  }
  if (err.includes('Gmail red/tiempo') || low.includes('fetch failed')) {
    return 'Google no respondió a tiempo. Se reintenta en la próxima lectura.';
  }
  if (err.includes('Clave de cuenta de servicio')) {
    return 'La clave de acceso del asistente no se pudo leer en el servidor.';
  }
  return 'Falló la última lectura.';
}

function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function LastRunLine({ run }: { run: LastRun | null }) {
  if (!run) return <span>Sin corridas registradas</span>;
  const nuevos = num(run.stats, 'new');
  const errores = num(run.stats, 'errors');
  const parts = [`Última corrida: ${runStatusText(run.status)}`];
  if (nuevos !== null) parts.push(`${nuevos.toLocaleString()} nuevos`);
  if (errores !== null) parts.push(`${errores.toLocaleString()} errores`);
  if (run.status !== 'running' && run.duration_ms != null) parts.push(durationText(run.duration_ms));
  return (
    <span className={run.status === 'failed' || (errores ?? 0) > 0 ? 'text-[var(--color-error)]' : undefined}>
      {parts.join(' · ')}
    </span>
  );
}

/** Tipos de correo con acción real para el vendedor (configuración del asistente). */
function HandlerTypes({ types }: { types: string[] | undefined }) {
  const list = types ?? [];
  return (
    <div className="mt-2">
      <div className="text-xs text-[var(--color-text-secondary)] mb-1">Tipos con acción real</div>
      {list.length === 0 ? (
        <span className="text-xs text-[var(--color-text-muted)]">
          Ningún tipo habilitado: aunque apagues el aprendizaje, nada se ejecuta de verdad
        </span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {list.map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]"
            >
              {typeLabel(t)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MailboxesTab() {
  const sellers = useUIStore((s) => s.sellers);
  const [rows, setRows] = useState<MailboxWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [now, setNow] = useState(() => Date.now());
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqSeq = useRef(0);
  const [meta, setMeta] = useState<AssistantMeta | null>(null);
  // Casilla que espera la confirmación para apagar el modo aprendizaje (segundo clic).
  const [confirmLearningOff, setConfirmLearningOff] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    void loadMeta().then((m) => {
      if (alive && m) setMeta(m);
    });
    return () => {
      alive = false;
    };
  }, []);

  const showToast = (t: { kind: 'ok' | 'err'; msg: string }) => {
    setToast(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    []
  );

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number) => map.get(id) ?? String(id);
  }, [sellers]);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await api.get<MailboxWithStats[]>('/assistant/mailboxes');
      if (seq !== reqSeq.current) return;
      if (res.success && res.data) {
        setRows(res.data);
        setError(null);
      } else {
        setError(res.error || 'No se pudieron cargar las casillas.');
      }
    } catch {
      if (seq === reqSeq.current) setError('No se pudieron cargar las casillas.');
    } finally {
      if (seq === reqSeq.current) {
        setLoading(false);
        setNow(Date.now());
      }
    }
  }, []);

  // Carga inicial + auto-refresco cada 30 s (se limpia al salir de la pestaña).
  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const setFlag = async (m: MailboxWithStats, field: 'enabled' | 'learning_mode', value: boolean) => {
    const key = `${m.id}:${field}`;
    if (field === 'learning_mode') setConfirmLearningOff(null);
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await api.patch(`/assistant/mailboxes/${m.id}`, { [field]: value });
      if (res.success) {
        setRows((rs) => rs.map((r) => (r.id === m.id ? { ...r, [field]: value } : r)));
        const msg =
          field === 'enabled'
            ? value
              ? 'Lectura activada'
              : 'Lectura apagada'
            : value
              ? 'Modo aprendizaje activado: el asistente solo anota lo que haría, sin ejecutarlo.'
              : 'Modo aprendizaje apagado: los tipos habilitados ya ejecutan acciones de verdad.';
        showToast({ kind: 'ok', msg });
        void load();
      } else {
        showToast({ kind: 'err', msg: res.error || 'No se pudo actualizar la casilla.' });
      }
    } catch {
      showToast({ kind: 'err', msg: 'No se pudo actualizar la casilla.' });
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const runNow = async (m: MailboxWithStats) => {
    const key = `${m.id}:run`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await api.post(`/assistant/mailboxes/${m.id}/run`, {});
      if (res.success) {
        showToast({ kind: 'ok', msg: `Lectura lanzada para ${m.address}` });
        void load();
      } else {
        showToast({ kind: 'err', msg: res.error || 'No se pudo lanzar la lectura.' });
      }
    } catch {
      showToast({ kind: 'err', msg: 'No se pudo lanzar la lectura.' });
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Se actualiza sola cada 30 segundos.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          title="Actualizar"
          aria-label="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {toast && (
        <div
          className={`rounded-lg p-3 text-sm ${
            toast.kind === 'ok'
              ? 'bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]'
              : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && !error ? (
        <div className="flex items-center justify-center py-12 text-[var(--color-text-secondary)]">
          <Loader2 size={18} className="animate-spin mr-2" /> Cargando…
        </div>
      ) : rows.length === 0 && !error ? (
        <div className="text-center py-8 text-[var(--color-text-secondary)]">No hay casillas configuradas.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((m) => {
            const { light, text } = lightOf(m, now);
            const lastMin = minutesSince(m.last_run?.started_at, now);
            const folders = m.folders ?? [];
            return (
              <div
                key={m.id}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 space-y-3"
              >
                {/* Encabezado: empresa, dirección, semáforo */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-[var(--color-text-secondary)]">Empresa</div>
                    <div className="font-semibold text-[var(--color-text)]">{sellerName(m.seller_id)}</div>
                    <div className="text-sm text-[var(--color-text-secondary)] truncate" title={m.address}>
                      {m.address}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0" title={text}>
                    <span className={`inline-block h-3 w-3 rounded-full ${LIGHT_DOT[light]}`} />
                    <span className="text-xs text-[var(--color-text-secondary)]">{text}</span>
                  </div>
                </div>

                {/* Estado de lectura */}
                <div className="space-y-1 text-sm text-[var(--color-text)]">
                  <div>
                    Última lectura:{' '}
                    <span title={fmtDateTime(m.last_run?.started_at)}>{agoText(lastMin)}</span>
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    <LastRunLine run={m.last_run} />
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    {m.last_sync_at
                      ? `Al día hasta: ${fmtDateTime(m.last_sync_at)}`
                      : 'Carga inicial en curso (últimos 60 días)'}
                  </div>
                  {m.last_error && (
                    <div
                      className={`text-xs ${
                        light === 'red' ? 'text-[var(--color-error)]' : 'text-[var(--color-warning)]'
                      }`}
                      title={m.last_error}
                    >
                      {errorText(m.last_error)}
                    </div>
                  )}
                  <div>
                    Correos cargados:{' '}
                    <span className="font-semibold">{m.emails_total.toLocaleString()}</span>
                  </div>
                </div>

                {/* Carpetas detectadas */}
                <div>
                  <div className="text-xs text-[var(--color-text-secondary)] mb-1">Carpetas detectadas</div>
                  {folders.length === 0 ? (
                    <span className="text-xs text-[var(--color-text-muted)]">Ninguna todavía</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {folders.map((f) => (
                        <span
                          key={f.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]"
                        >
                          <Folder size={10} /> {f.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Controles (solo quien puede operar el asistente) */}
                <Can
                  cap="assistant.manage"
                  fallback={
                    <div className="text-xs text-[var(--color-text-secondary)] border-t border-[var(--color-border)] pt-3">
                      Lectura activa: {m.enabled ? 'sí' : 'no'} · Modo aprendizaje: {m.learning_mode ? 'sí' : 'no'}
                    </div>
                  }
                >
                  <div className="space-y-2 border-t border-[var(--color-border)] pt-3">
                    <label className="flex items-center justify-between gap-2 text-sm text-[var(--color-text)]">
                      Lectura activa
                      <Switch
                        label="Lectura activa"
                        checked={m.enabled}
                        disabled={!!busy[`${m.id}:enabled`]}
                        onChange={(v) => void setFlag(m, 'enabled', v)}
                      />
                    </label>
                    <div>
                      <label className="flex items-center justify-between gap-2 text-sm text-[var(--color-text)]">
                        Modo aprendizaje
                        <Switch
                          label="Modo aprendizaje"
                          checked={m.learning_mode}
                          disabled={!!busy[`${m.id}:learning_mode`]}
                          onChange={(v) => {
                            // Encender no pide confirmación; apagar sí (segundo clic en "Sí, apagar").
                            if (v) void setFlag(m, 'learning_mode', true);
                            else setConfirmLearningOff(m.id);
                          }}
                        />
                      </label>
                      {confirmLearningOff === m.id && m.learning_mode && (
                        <div className="mt-2 rounded-lg border border-[var(--color-warning)] p-2 space-y-2">
                          <p className="text-xs text-[var(--color-text)]">
                            Al apagarlo, los tipos habilitados en la configuración ejecutan acciones de verdad sobre
                            Mercado Libre y la contabilidad. ¿Confirmás?
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => void setFlag(m, 'learning_mode', false)}
                              disabled={!!busy[`${m.id}:learning_mode`]}
                              className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-warning)] text-white hover:opacity-90 disabled:opacity-50"
                            >
                              {busy[`${m.id}:learning_mode`] && <Loader2 size={12} className="animate-spin" />}
                              Sí, apagar
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmLearningOff(null)}
                              className="px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                        {m.learning_mode
                          ? 'Encendido: el asistente decide qué haría con cada correo y lo anota en el Registro como “simulado”, sin tocar nada.'
                          : 'Apagado: los tipos de correo de abajo ejecutan acciones de verdad; el resto se sigue simulando.'}
                      </p>
                      <HandlerTypes types={meta?.handler_types_by_seller?.[String(m.seller_id)]} />
                    </div>
                    <button
                      type="button"
                      onClick={() => void runNow(m)}
                      disabled={!m.enabled || !!busy[`${m.id}:run`]}
                      title={m.enabled ? 'Leer la casilla ahora' : 'La lectura está apagada'}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {busy[`${m.id}:run`] ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                      Leer ahora
                    </button>
                  </div>
                </Can>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
